// Откуда берётся картинка человека — ОДНО правило на приложение.
//
// ─────────────────────────────────────────────────────────────────────────────
// Почему это отдельный файл, а не выражение по месту
// ─────────────────────────────────────────────────────────────────────────────
// Поверхностей четыре (кнопка меню профиля, шапка внутри раскрытого меню,
// мобильная шторка, форма профиля), и до 23.08.2026 они читали РАЗНОЕ: три
// брали локальное зеркало, одна — сводку профиля экосистемы. Итог был ровно
// такой, каким его увидел владелец: в одном месте фото, в другом буквенный
// кружок, и объяснить это нельзя ничем, кроме истории кода.
//
// Решение владельца: во всех сервисах экосистемы показывается ОДНА картинка —
// та, что отдаёт единый профиль. Своё фото и своя заглушка отменены.
//
// ─────────────────────────────────────────────────────────────────────────────
// Приоритет считает СЕРВЕР, здесь его нет
// ─────────────────────────────────────────────────────────────────────────────
// Источников картинки два: локальное фото, загруженное человеком у нас, и
// зеркало единого профиля. Локальное ПЕРЕОПРЕДЕЛЯЕТ картинку экосистемы внутри
// сервиса и не заменяет фото в самом профиле (решение владельца 2026-08-23).
//
// Разрешать этот приоритет здесь было бы второй копией правила: сервер уже
// отдаёт готовый адрес в `avatarUrl` (`shared/utils/resolve-avatar-url.ts`).
// Клиенту остаётся выбрать между двумя ПРЕДСТАВЛЕНИЯМИ одного и того же
// значения — свежепрочитанной сводкой и тем, что приехало с профилем.
//
// ─────────────────────────────────────────────────────────────────────────────
// Своей заглушки у сервиса нет
// ─────────────────────────────────────────────────────────────────────────────
// Отдельного состояния «аватара нет» у экосистемы НЕ существует: адрес
// `https://id.iec.exchange/api/avatar/{userId}` есть всегда и всегда отдаёт
// картинку — фото или их собственную заглушку. Для нас разницы нет.
//
// Пусто у нас бывает по ДРУГОЙ причине: адрес несёт ИХ `userId`, которого мы не
// знаем и вывести не можем (наш `sub` — другое значение). Пока человек не вошёл
// после переезда и пока его сводка не отдала адрес, взять его неоткуда. Это
// переходное состояние, и оно закрывается догоном
// (`inngest/functions/iec-id/backfill-profile-mirror.ts`), а не заглушкой навсегда.

/** Хост канонического адреса. Стабилен целиком; версии в адресе нет. */
const CANONICAL_PREFIX = "https://id.iec.exchange/api/avatar/";

export interface AvatarSources {
	/** `avatarUrl` из профиля — сервер уже разрешил приоритет. */
	local?: string | null;
	/**
	 * Адрес из свежепрочитанной сводки, если поверхность её и так запрашивает.
	 *
	 * Не «второй источник», а тот же самый на пару минут раньше: сводка
	 * раскладывается в зеркало тем же `applyProfileSummary`. Нужен ровно там,
	 * где сводка уже в руках, — чтобы не ждать перезапроса профиля.
	 *
	 * ВАЖНО: сводка не знает о локальном фото, поэтому уступает ему. Отсюда
	 * порядок ниже: сначала локальное (если оно есть — сервер положил его в
	 * `local`), потом сводка.
	 */
	summary?: string | null;
	/** Локальное фото загружено — тогда сводка его перекрыть не имеет права. */
	hasLocalAvatar?: boolean;
}

/**
 * Адрес картинки человека. `null` — адреса ещё нет (см. шапку), и поверхность
 * показывает переходную заглушку.
 */
export function resolveUserAvatarUrl(sources: AvatarSources): string | null {
	const url = sources.hasLocalAvatar ? sources.local : (sources.summary ?? sources.local);
	return url?.trim() ? url : null;
}

/**
 * Адрес уже канонический?
 *
 * Нужно не для показа (показываем что есть), а для честности отчётов и тестов:
 * пока предикат ложен И локального фото нет, человек видит НЕ ту картинку, что
 * в остальных сервисах.
 */
export function isCanonicalAvatarUrl(url: string | null | undefined): boolean {
	return typeof url === "string" && url.startsWith(CANONICAL_PREFIX);
}
